import { ApiResponse, AxiosErrorCustom, axiosInstance } from '@/lib/axios';
import { TypedMutationConfig } from '@/lib/react-query';
import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';
import { AxiosResponse } from 'axios';
import { TLabelAuto } from '../types/label-auto.type';

/**
 *
 */
export const updateLabelAutoValidationSchema = z
  .object({
    labelAutoId: z.string().min(1),
    name: z.string().min(1).optional(),
    color: z.string().min(1).optional(),
    teamId: z.string().min(1).optional(),
    field: z.enum(['isCustomerAuthorized', 'totalFollower', 'content']).optional(),
    organizationChannelIds: z.array(z.string().min(1)).min(1).optional(),
    valueContent: z
      .object({
        type: z.enum(['or', 'and']),
        words: z.array(z.string().min(1)).min(1),
      })
      .optional(),
    valueTotalFollower: z.number().optional(),
    valueIsCustomerAuthorized: z.boolean().optional(),
  })
  .refine(
    (data) => {
      if (data.field === 'content') {
        return data.valueContent !== null && data.valueContent !== undefined;
      }
      return true;
    },
    {
      message: 'Content value is required when field is content',
      path: ['valueContent'],
    }
  )
  .refine(
    (data) => {
      if (data.field === 'totalFollower') {
        return data.valueTotalFollower !== null && data.valueTotalFollower !== undefined;
      }
      return true;
    },
    {
      message: 'Total follower value is required when field is total follower',
      path: ['valueTotalFollower'],
    }
  )
  .refine(
    (data) => {
      if (data.field === 'isCustomerAuthorized') {
        return data.valueIsCustomerAuthorized !== null && data.valueIsCustomerAuthorized !== undefined;
      }
      return true;
    },
    {
      message: 'User type value is required when field is user type',
      path: ['valueIsCustomerAuthorized'],
    }
  );
export type TUpdateLabelAutoValidation = z.infer<typeof updateLabelAutoValidationSchema>;
export type TUpdateLabelAutoPayload = TUpdateLabelAutoValidation;
export type TUpdateLabelAutoResponse = AxiosResponse<ApiResponse<TLabelAuto>>;

export const updateLabelAuto = async (payload: TUpdateLabelAutoPayload): Promise<TUpdateLabelAutoResponse> => {
  const url = `/auto-label/${payload.labelAutoId}`;

  const toBeSendData: Partial<{
    name: string;
    color: string;
    teamId: string;
    field: 'isCustomerAuthorized' | 'totalFollower' | 'content';
    organizationChannelIds: string[];
    value: { type: 'or' | 'and'; words: string[] } | number | boolean | undefined;
  }> = {};

  // Only add fields that are provided in the payload
  if (payload.name !== undefined) toBeSendData.name = payload.name;
  if (payload.color !== undefined) toBeSendData.color = payload.color;
  if (payload.teamId !== undefined) toBeSendData.teamId = payload.teamId;
  if (payload.field !== undefined) toBeSendData.field = payload.field;
  if (payload.organizationChannelIds !== undefined)
    toBeSendData.organizationChannelIds = payload.organizationChannelIds || [];

  // Handle value assignment based on field type
  if (payload.field === 'content' && payload.valueContent) {
    toBeSendData.value = {
      type: payload.valueContent.type,
      words: payload.valueContent.words,
    };
  } else if (payload.field === 'totalFollower') {
    toBeSendData.value = payload.valueTotalFollower ?? undefined;
  } else if (payload.field === 'isCustomerAuthorized') {
    toBeSendData.value = payload.valueIsCustomerAuthorized ?? undefined;
  }
  return axiosInstance.patch(url, toBeSendData);
};

/**
 * Use Update Label Auto React-Query Hook
 */
type TUseUpdateLabelAutoMutationOptions = {
  mutationConfigOptions?: TypedMutationConfig<typeof updateLabelAuto, AxiosErrorCustom>;
};

export const useUpdateLabelAutoMutation = ({ mutationConfigOptions }: TUseUpdateLabelAutoMutationOptions) => {
  const { ...restConfig } = mutationConfigOptions || {};

  return useMutation({
    ...restConfig,
    mutationFn: updateLabelAuto,
  });
};
